import { type FC } from "react";

import { type BlackboardInstance } from "../blackboardModels";
import { formatTokenCount } from "./helpers";

/**
 * Context-window growth-toward-compact bar. Lifted verbatim from the former
 * InfoTab god component. Progress is based on lifetime context growth since
 * last evolution; the bar turns amber within 80% of the compact threshold.
 */
export const ContextWindowDisplay: FC<{
  contextWindow: NonNullable<BlackboardInstance["contextWindow"]>;
  compactThreshold: number;
}> = ({ contextWindow, compactThreshold }) => {
  const {
    contextGrowthSinceEvolution,
    contextWindowSize,
    currentPhase,
    phaseBudgetRemaining,
  } = contextWindow;

  const pct = Math.min(
    (contextGrowthSinceEvolution / compactThreshold) * 100,
    100,
  );
  const isNearFull = pct >= 80;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[11px] font-bold tabular-nums text-violet-600 dark:text-violet-400">
          {formatTokenCount(contextWindowSize)}
        </span>
        <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
          total · {currentPhase}
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        <div
          className={`h-full transition-all duration-500 ${
            isNearFull ? "bg-amber-500" : "bg-violet-500/70"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
          {formatTokenCount(contextGrowthSinceEvolution)} /{" "}
          {formatTokenCount(compactThreshold)} to compact
        </span>
        {phaseBudgetRemaining <= 5000 && currentPhase === "act" && (
          <span className="font-mono text-[10px] text-amber-500 dark:text-amber-400">
            compact soon
          </span>
        )}
      </div>
    </div>
  );
};
